import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReusableTable from "../../common/table/CommonTable";
import {
  fetchAdmissions,
  resetCurrentAdmission,
  updateAdmission,
} from "../../redux/admissions/admissionsActions";

const ListAdmissions = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const admissionsList = useSelector(
    (state) => state?.admission?.fetchAdmissions || {}
  );
  const updateAdmissionState = useSelector(
    (state) => state?.admission?.updateAdmission || {}
  );
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dischargeDialogOpen, setDischargeDialogOpen] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = admissionsList?.admissions?.totalPages || 0;

  const fetchData = async (page) => {
    await dispatch(fetchAdmissions(page));
    setLoading(false);
  };
  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, dispatch]);

  useEffect(() => {
    if (
      admissionsList.admissions?.data &&
      Array.isArray(admissionsList.admissions.data)
    ) {
      const formattedData = admissionsList.admissions.data.map((admission) => ({
        id: admission.id,
        admissionNo: admission.admission_no,
        patientId: admission.Patient?.id,
        patientSerialNo: admission.Patient?.patient_serial_no,
        patientName: admission.Patient?.patient_name,
        ipNumber: admission.Patient?.ip_number,
        phone: admission.Patient?.phone,
        admitDate: admission.admit_date,
        dischargeDate: admission.discharge_date,
        blockName: admission.Block?.block_name || "N/A",
        floorName: admission.Floor?.floor_name || "N/A",
        departmentName: admission.Department?.department_name || "N/A",
        wardName: admission.Ward?.ward_name || "N/A",
        status: getStatusLabel(admission.admission_status),
        statusCode: admission.admission_status,
        numAttenders: admission.number_attenders || 0,
        attenders: admission.Attenders || [],
      }));
      setData(formattedData);
      setFilteredData(formattedData);
    } else {
      setData([]);
      setFilteredData([]);
    }
  }, [admissionsList]);

  useEffect(() => {
    if (updateAdmissionState.success) {
      setSuccessSnackbarOpen(true);
      dispatch(resetCurrentAdmission());
      fetchData(currentPage);
    }
  }, [updateAdmissionState.success, dispatch]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredData(data);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = data.filter(
        (item) =>
          String(item.admissionNo).toLowerCase().includes(lowercasedSearch) ||
          String(item.patientSerialNo)
            .toLowerCase()
            .includes(lowercasedSearch) ||
          String(item.patientName).toLowerCase().includes(lowercasedSearch) ||
          String(item.ipNumber).toLowerCase().includes(lowercasedSearch) ||
          String(item.phone).toLowerCase().includes(lowercasedSearch) ||
          String(item.departmentName)
            .toLowerCase()
            .includes(lowercasedSearch) ||
          String(item.wardName).toLowerCase().includes(lowercasedSearch) ||
          String(item.status).toLowerCase().includes(lowercasedSearch)
      );
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  const getStatusLabel = (statusCode) => {
    switch (statusCode) {
      case 1:
        return "Active";
      case 2:
        return "In Active";
      case 3:
        return "Discharged";
      default:
        return "Unknown";
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleDischargeConfirm = () => {
    if (selectedAdmission) {
      const currentDate = new Date();
      const formattedDate = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(
        2,
        "0"
      )} ${String(currentDate.getHours()).padStart(2, "0")}:${String(
        currentDate.getMinutes()
      ).padStart(2, "0")}`;
      const formdata = {
        admission_status: "3",
        discharge_date: formattedDate,
      };

      dispatch(updateAdmission(selectedAdmission.id, formdata));

      setDischargeDialogOpen(false);
    }
  };

  const handleDischargeCancel = () => {
    setDischargeDialogOpen(false);
    setSelectedAdmission(null);
  };

  const handleCloseSuccessSnackbar = () => {
    setSuccessSnackbarOpen(false);
  };
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const columns = [
    { id: "admissionNo", label: "Admission No", sortable: true },
    { id: "patientSerialNo", label: "Patient ID", sortable: true },
    { id: "patientName", label: "Patient Name", sortable: true },
    { id: "ipNumber", label: "IP Number", sortable: true },
    { id: "phone", label: "Phone", sortable: true },
    {
      id: "admitDate",
      label: "Admit Date",
      sortable: true,
      render: (value) => {
        const date = new Date(value);
        return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
      },
    },
    {
      id: "dischargeDate",
      label: "Discharge Date",
      sortable: true,
      render: (value) => {
        if (!value) return "Not Discharged";
        const date = new Date(value);
        return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
      },
    },
    { id: "departmentName", label: "Department", sortable: true },
    { id: "wardName", label: "Ward", sortable: true },
    { id: "numAttenders", label: "Attenders", sortable: true },
    {
      id: "status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color={
            value === "Active"
              ? "success"
              : value === "In Active"
              ? "default"
              : value === "Discharged"
              ? "primary"
              : "warning"
          }
        />
      ),
    },
  ];

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Search Admission
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by patient name, ID, admission number, ward, department, etc."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Paper>

      <ReusableTable
        title="Admissions"
        columns={columns}
        data={filteredData}
        isLoading={loading}
        headerColor="#000"
        rowColor="#ffffff"
        alternateRowColor="#f5f5f5"
        withPagination={true}
        withActions={true}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        showDownloadOption={true}
      />
      <Dialog
        open={dischargeDialogOpen}
        onClose={handleDischargeCancel}
        aria-labelledby="discharge-dialog-title"
      >
        <DialogTitle
          id="discharge-dialog-title"
          sx={{ bgcolor: "red", color: "white" }}
        >
          Confirm Discharge
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            Are you sure you want to discharge this patient?
            {selectedAdmission && (
              <Box component="span" fontWeight="bold">
                {" "}
                {selectedAdmission.patientName}
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDischargeCancel}
            sx={{
              backgroundColor: "orange",
              color: "white",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDischargeConfirm}
            variant="contained"
            sx={{
              backgroundColor: "green",
              color: "white",
            }}
          >
            Discharge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSuccessSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSuccessSnackbar}
          severity="success"
          sx={{ width: "100%", bgcolor: "green", color: "white" }}
        >
          Patient discharged successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListAdmissions;
